/*
 * /start — "Your first event is on us" (design 2026-10-13-free-start-web r9,
 * W1, look 1A). Email, its code, phone, its code, name; then the account is
 * signed in and lands on the 6A first run in /channels.
 *
 * Rendered only from GET /api/v3/free_start (Req 16): with the offer off, failed
 * or timed out this page sends a visitor to the marketing pricing page. Every call lives in
 * utils/api/freeStart.js.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Helmet, HelmetProvider } from "react-helmet-async";
import Logo from "components/Logo";
import { ft, fw, Spinner, Photo, Ticket, TicketStrip, STAMPS } from "components/FreeEvent";
import { noWidow } from "utils/eventTime";
import { DEFAULT_COUNTRY, displayPhone } from "components/FreeEvent/PhoneField";
import { setAuthToken, fetchSession } from "containers/App/actions";
import { selectAuthToken } from "containers/App/selectors";
import { setItem } from "utils/localStorage";
import { MARKETING_PRICING_URL, MARKETING_HOME_URL } from "utils/marketing";
import {
  getFreeStart,
  postFreeStartCode,
  postFreeStartVerify,
  postPhoneCode,
  postPhoneVerify,
  postFreeStartAccount,
  postResumeLink,
  postResume,
} from "utils/api/freeStart";
import { trackStartViewed, trackStepCompleted } from "utils/freeEventAnalytics";
import { captureUtm, clearUtm } from "./utm";
import { classify } from "./errors";
import { EmailStep, CodeStep, PhoneStep, NameStep } from "./steps";
import "./free-start.css";

// Which ticket stamp each step is on.
const STAMP_OF = { email: "email", emailCode: "email", phone: "phone", phoneCode: "phone", name: "name" };
const DONE_OF = { email: 0, emailCode: 0, phone: 1, phoneCode: 1, name: 2 };

// The three guest prints scattered behind the ticket (sheet tools/build.py).
const PRINTS = [
  ["guest-pair", { left: 6, top: 34, transform: "rotate(-8deg)" }],
  ["guest-solo-a", { left: 252, top: 6, transform: "rotate(7deg)" }],
  ["guest-solo-b", { left: 262, top: 368, transform: "rotate(-4deg)" }],
];

// The answer to a request the customer has since walked away from.
const STALE = Symbol("stale");

// How long a cold visitor waits on the offer check before the page gives up.
export const OFFER_TIMEOUT_MS = 8000;

// Fail-closed: anything but a clear "enabled" (off, 404, error, timeout) is
// the offer closed, so a visitor never sits on a dead page.
const closedOnFailure = (request) =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ enabled: false }), OFFER_TIMEOUT_MS);
    request().then(
      (res) => {
        clearTimeout(timer);
        resolve(res);
      },
      () => {
        clearTimeout(timer);
        resolve({ enabled: false });
      }
    );
  });

// Offer closed: a cold visitor goes to the plans page, a signed-in user to
// their dashboard.
export const OfferOff = ({ signedIn }) => {
  const navigate = useNavigate();
  useEffect(() => {
    if (signedIn) navigate("/channels", { replace: true });
    else window.location.replace(MARKETING_PRICING_URL);
  }, [signedIn, navigate]);
  return (
    <div className="redirect" role="status">
      <Spinner />
      <h1>{ft("em.off")}</h1>
      <a href={signedIn ? "/channels" : MARKETING_PRICING_URL}>{ft("em.off.a")}</a>
    </div>
  );
};

const Lapsed = () => (
  <>
    <h1 className="st-h">{ft("la.h")}</h1>
    <p className="st-lead">{fw("la.p")}</p>
    <a className="btn" href={MARKETING_PRICING_URL}>
      {ft("la.btn")}
    </a>
  </>
);

const Skeleton = () => (
  <div className="st-skel" aria-busy="true" aria-label={ft("em.h")}>
    <div className="skel" style={{ width: "40%", height: 14 }} />
    <div className="skel" style={{ width: "70%", height: 36 }} />
    <div className="skel" style={{ width: "90%", height: 18, marginBottom: 28 }} />
    <div className="skel" style={{ width: "100%", height: 52 }} />
    <div className="skel" style={{ width: "100%", height: 52 }} />
  </div>
);

const FreeStart = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [utm] = useState(() => captureUtm(location.search));
  const resumeToken = params.get("resume");

  const signedIn = Boolean(useSelector(useMemo(() => selectAuthToken(), [])));
  const offer = useQuery(["freeStart", "offer"], () => closedOnFailure(getFreeStart), {
    retry: false,
    // Only the first answer decides: a later refetch (focus, remount) that
    // failed would otherwise redirect a visitor out of a signup in progress.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const [step, setStep] = useState(resumeToken ? "resume" : "email");
  const [notice, setNotice] = useState(null);
  const [off, setOff] = useState(false);
  const [email, setEmail] = useState(() => params.get("email") || "");
  const [emailMeta, setEmailMeta] = useState({});
  const [signupToken, setSignupToken] = useState(null);
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [phoneMeta, setPhoneMeta] = useState({});
  const [firstName, setFirstName] = useState("");
  const [company, setCompany] = useState("");

  useEffect(() => {
    trackStartViewed();
  }, []);

  // Bumped on every step change. A verify remembers the value it started
  // under and drops its answer (success or error) once the customer has moved
  // on, e.g. tapped "Change email" while it was pending: the old address's
  // signup ticket or sign-in must never land over the details that replaced it.
  const attempt = useRef(0);
  const go = (next) => {
    attempt.current += 1;
    setStep(next);
    window.scrollTo(0, 0);
  };
  // Runs `request`, then hands back its result only if the step it was sent
  // from is still the current one; `STALE` otherwise.
  const unlessStale = async (request) => {
    const at = attempt.current;
    try {
      const res = await request();
      return attempt.current === at ? res : STALE;
    } catch (err) {
      if (attempt.current !== at) return STALE;
      throw err;
    }
  };

  // The account is signed in the way SignInPage does it; /channels shows the
  // 6A first run (or the returner/no-free-event note) from this state.
  const signIn = (token, outcome) => {
    clearUtm();
    setItem("authToken", token);
    dispatch(setAuthToken(token));
    navigate("/channels", { replace: true, state: { freeStart: outcome, firstName: firstName.trim() || undefined } });
    dispatch(fetchSession());
  };

  // Outcomes every step shares. True when handled here.
  const onShared = useCallback((c) => {
    if (c.kind === "off") {
      setOff(true);
      return true;
    }
    if (c.kind === "lapsed") {
      go("lapsed");
      return true;
    }
    if (c.kind === "restart") {
      setSignupToken(null);
      setNotice("restart");
      go("email");
      return true;
    }
    if (c.kind === "rejected" && c.code === "account_exists") {
      navigate("/sign-in", { replace: true });
      return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 4A: the emailed link reopens the flow at the phone step.
  const resumed = useRef(false);
  useEffect(() => {
    if (!resumeToken || resumed.current) return;
    resumed.current = true;
    postResume({ token: resumeToken })
      .then((res) => {
        setEmail(res.email || "");
        setSignupToken(res.signup_token);
        go("phone");
      })
      .catch((err) => {
        const c = classify(err);
        if (c.kind === "off") setOff(true);
        else {
          setNotice("restart");
          go("email");
        }
      });
  }, [resumeToken]);

  const sendEmail = async (value) => {
    const res = await postFreeStartCode({ email: value, utm });
    setEmail(value);
    setEmailMeta({ resendAfter: res && res.resend_after, at: Date.now() });
    setNotice(null);
    trackStepCompleted("email");
    go("emailCode");
  };

  const verifyEmail = async (code) => {
    const res = await unlessStale(() => postFreeStartVerify({ email, code, utm }));
    if (res === STALE) return;
    trackStepCompleted("email_code");
    if (res && res.authentication_token) {
      // A returner (or an account the email alone completed) signs straight in.
      const outcome = res.existing_account ? "back" : res.free_event_granted === false ? "none" : "new";
      signIn(res.authentication_token, outcome);
      return;
    }
    setSignupToken(res.signup_token);
    go("phone");
  };

  const sendPhone = async (e164) => {
    const res = await postPhoneCode({ signupToken, phone: e164 });
    setPhoneMeta({ e164, resendAfter: res && res.resend_after, channel: (res && res.channel) || "sms", at: Date.now() });
    trackStepCompleted("phone");
    go("phoneCode");
  };

  const verifyPhone = async (code) => {
    const res = await unlessStale(() => postPhoneVerify({ signupToken, phone: phoneMeta.e164, code }));
    if (res === STALE) return;
    trackStepCompleted("phone_code");
    go("name");
  };

  const createAccount = async () => {
    const res = await postFreeStartAccount({ signupToken, firstName: firstName.trim(), company: company.trim(), utm });
    trackStepCompleted("name");
    signIn(res.authentication_token, res.free_event_granted === false ? "none" : "new");
  };

  if (off || (offer.data && offer.data.enabled === false)) {
    return (
      <div className="mb-fs start">
        <OfferOff signedIn={signedIn} />
      </div>
    );
  }

  const loading = offer.isLoading || step === "resume";
  const call = phoneMeta.channel === "call";
  const heads = {
    email: { eb: "em.eb", h: "em.h", lead: ft("em.lead") },
    emailCode: { eb: "em.eb", h: "ec.h", lead: ft("ec.lead", { email }), change: "ec.change", back: "email" },
    phone: { eb: "ph.eb", h: "ph.h", lead: ft("ph.lead") },
    phoneCode: {
      eb: "ph.eb",
      h: call ? "pc.call.h" : "pc.h",
      lead: call ? ft("pc.call", { phone: displayPhone(phoneMeta.e164) }) : ft("pc.lead", { phone: displayPhone(phoneMeta.e164) }),
      change: "pc.change",
      back: "phone",
    },
    name: { eb: "nm.eb", h: "nm.h", lead: ft("nm.lead") },
  };
  const head = heads[step];

  let body;
  if (loading) body = <Skeleton />;
  else if (step === "lapsed") body = <Lapsed />;
  else {
    body = (
      <>
        <p className="eyebrow">{ft(head.eb)}</p>
        <h1 className="st-h">{fw(head.h)}</h1>
        <p className="st-lead">
          {noWidow(head.lead)}
          {head.change && (
            <>
              {" "}
              <button type="button" className="linkish" onClick={() => go(head.back)}>
                {ft(head.change)}
              </button>
            </>
          )}
        </p>
        {step === "email" && (
          <EmailStep email={email} onEmail={setEmail} notice={notice} send={sendEmail} onShared={onShared} />
        )}
        {step === "emailCode" && (
          <CodeStep
            key={`e${emailMeta.at}`}
            kind="email"
            stepName="email_code"
            resendAfter={emailMeta.resendAfter}
            verify={verifyEmail}
            resend={() => postFreeStartCode({ email, utm })}
            onShared={onShared}
          />
        )}
        {step === "phone" && (
          <PhoneStep
            phone={phone}
            onPhone={setPhone}
            country={country}
            onCountry={setCountry}
            email={email}
            sendCode={sendPhone}
            sendLater={() => postResumeLink({ signupToken, utm })}
            onShared={onShared}
          />
        )}
        {step === "phoneCode" && (
          <CodeStep
            key={`p${phoneMeta.at}`}
            kind={call ? "call" : "phone"}
            stepName="phone_code"
            resendAfter={phoneMeta.resendAfter}
            verify={verifyPhone}
            resend={() => postPhoneCode({ signupToken, phone: phoneMeta.e164 })}
            onShared={onShared}
          />
        )}
        {step === "name" && (
          <NameStep
            firstName={firstName}
            onFirstName={setFirstName}
            company={company}
            onCompany={setCompany}
            create={createAccount}
            onShared={onShared}
          />
        )}
      </>
    );
  }

  const stampKey = STAMP_OF[step] || "email";
  const host = step === "name" && company.trim() ? ft("t.host", { company: company.trim() }) : ft("t.host0");

  return (
    <div className="mb-fs start">
      <HelmetProvider>
        <Helmet title={`Movebooth | ${ft(head ? head.h : "em.h")}`} />
      </HelmetProvider>
      <header className="st-top">
        <a href={MARKETING_HOME_URL} aria-label="Movebooth">
          <Logo withText scale={0.8} />
        </a>
        <p className="r">
          <span className="hide-s">{ft("top.q")} </span>
          <Link to="/sign-in">{ft("top.signin")}</Link>
        </p>
      </header>
      <main className="st-body">
        <div className="st-form">
          {step !== "lapsed" && <TicketStrip done={DONE_OF[step] || 0} />}
          {step === "email" && <p className="st-foot st-cond">{ft("st.cond")}</p>}
          {body}
        </div>
        <aside className="st-aside" aria-hidden="true">
          <div className="ticket-wrap">
            {PRINTS.map(([slot, style]) => (
              <div key={slot} className="print" style={style}>
                <Photo slot={slot} />
              </div>
            ))}
            <Ticket stamps={STAMPS[stampKey]} title={ft("t.h")} host={host} />
          </div>
        </aside>
      </main>
    </div>
  );
};

export default FreeStart;
